import { formatAssetAmount } from '@exodus/formatting/lib/asset';
import FramedIcon from '@passkeys/ui/framed-icon';
import Skeleton from '@passkeys/ui/skeleton';
import Text from '@passkeys/ui/text';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import ActivityStatus from '@/ui/components/activity-status';
import selectors from '@/ui/flux/selectors';
import type {
  ActivityEntry,
  LabelEntry,
} from '@/ui/flux/tx-log/selectors/create-filtered-activity';
import type { ActivityItem } from '@/ui/flux/tx-log/types';
import { getIsFailed } from '@/ui/flux/tx-log/utils';
import useConvertedAmount from '@/ui/hooks/use-converted-amount';
import { formatFiat } from '@/ui/utils/formatting';
import { useNavigate } from '@/ui/utils/navigation';
import { formatDate } from '@/utils/datetime';

// TODO: Dedupe.
function LabelRow({ label }: LabelEntry) {
  return (
    <div className="mb-3 mt-6 w-fit rounded-full bg-black/3 px-2 py-0.5 theme-dark:bg-white/3">
      <Text className="theme-dark:text-white/40" monospace size={12}>
        {label}
      </Text>
    </div>
  );
}

type TxRowProps = {
  activityItem: ActivityItem;
};

// TODO: Dedupe.
function TxRow({ activityItem }: TxRowProps) {
  const { title, date, icon, order, assetName, displayAmount, id } =
    activityItem;

  const asset = useSelector(
    selectors.assets.createAssetSelector(assetName),
  ) as any;
  const orderAmount = order
    ? order.toAsset === assetName
      ? order.toAmount
      : order.fromAmount
    : null;
  const amountToUse = orderAmount || displayAmount;
  const fiatAmount = useConvertedAmount({
    amount: amountToUse,
  });

  const isFailed = getIsFailed(activityItem);

  const navigate = useNavigate();

  const handleClick = useCallback(() => {
    navigate(`/transactions/${id}`);
  }, [navigate, id]);

  return (
    <button
      type="button"
      className="relative -mx-4 -mb-px flex gap-4 rounded-xl border-y border-transparent p-4 transition-colors before:absolute before:inset-x-4 before:-top-px before:border-t before:border-solid before:border-black/6 before:transition-opacity hover:bg-black/6 hover:before:opacity-0 theme-dark:before:border-transparent theme-dark:hover:bg-white/6 [&:hover+button]:before:opacity-0"
      onClick={handleClick}
    >
      <FramedIcon className="text-black" name={icon} size={24} forceLight />

      <div className="flex flex-1 flex-col gap-1">
        <div className="flex items-center gap-x-1">
          <Text size={14}>{title}</Text>
          {isFailed && <ActivityStatus value="failed" size="small" />}
        </div>

        <Text size={14} className="text-gray-500 theme-dark:text-neutral-100">
          {formatDate(date, { short: true, noYear: true })}
        </Text>
      </div>

      <div className="flex flex-col gap-1">
        {amountToUse ? (
          <Text size={14} align="right">
            {formatAssetAmount(amountToUse as any, {
              useGrouping: true,
              maxSignificant: 6,
            })}{' '}
            {asset.displayTicker}
          </Text>
        ) : null}

        {fiatAmount ? (
          <Text
            size={14}
            align="right"
            className="text-gray-400 theme-dark:text-neutral-200"
          >
            {formatFiat(fiatAmount)}
          </Text>
        ) : null}
      </div>
    </button>
  );
}

function ActivitySkeleton() {
  return (
    <>
      <Skeleton
        width={124}
        height={16}
        className="mb-3 mt-6"
        borderRadius={9999}
      />

      <Skeleton height={44} className="my-4 border-t border-transparent" />
    </>
  );
}

type ActivityProps = {
  activity: ActivityEntry[];
  loading?: boolean;
};

function Activity({ activity, loading }: ActivityProps) {
  return (
    <div className="box-content flex w-full flex-col overflow-y-auto px-4 pb-4 scrollbar-hide">
      {loading ? (
        <ActivitySkeleton />
      ) : (
        activity.map((entry) =>
          entry.type === 'label' ? (
            <LabelRow key={entry.id} {...entry} />
          ) : (
            <TxRow key={entry.id} activityItem={entry} />
          ),
        )
      )}
    </div>
  );
}

export default Activity;
